import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';

import { Gate } from '../../../global/contexts/auth/auth';
import { fmtMoney } from '../../../common/utils/money';
import { useBatches, useGenerateBatch } from '../contexts/liquidations';

import '../styles/billing.css';

// Facturación · Liquidaciones: generar el batch del mes + listado por año. El DETALLE de un
// batch vive en su propia página (/admin/billing/liquidations/:id, batch-detail) — antes era
// un modal y las tablas se apeñuzcaban.
const MONTHS = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];
const STATUS = {
    draft:     { label: 'Borrador',       tone: 'open' },
    generated: { label: 'Generada',       tone: 'open' },
    posted:    { label: 'Contabilizada',  tone: 'good' },
    reversed:  { label: 'Reversada',      tone: 'bad' },
};

export default function LiquidationsPanel() {
    const [year, setYear] = useState(() => new Date().getFullYear());
    const [month, setMonth] = useState(() => new Date().getMonth() + 1);

    const { mutateAsync: generate, isPending: generating } = useGenerateBatch();
    const { data, isLoading, isFetching, error } = useBatches(useMemo(() => ({ year }), [year]));
    const batches = data?.items || [];

    const years = useMemo(() => { const y = new Date().getFullYear(); return [y, y - 1, y - 2]; }, []);

    const gen = async () => {
        try {
            const res = await generate({ year: Number(year), month: Number(month) });
            const count = res?.generated ?? res?.batch?.liquidation_count ?? 0;
            const errs = res?.errors?.length || 0;
            toast.success(`Liquidación de ${MONTHS[month - 1]} ${year}: ${count} línea(s)${errs ? ` · ${errs} con aviso` : ''}`);
        } catch (er) {
            if (er?.status === 409) toast.error('El período ya está contabilizado (requiere reversa)');
            else if (er?.status !== 403) toast.error(er?.message || 'No se pudo generar la liquidación');
        }
    };

    return (
        <>
            <Gate domain="billing" action="write">
                <div className="billing-generate">
                    <span className="billing-generate__title">Generar liquidación del mes</span>
                    <div className="billing-generate__row">
                        <select value={month} onChange={(e) => setMonth(Number(e.target.value))} disabled={generating}>
                            {MONTHS.map((m, i) => <option key={i} value={i + 1}>{m}</option>)}
                        </select>
                        <select value={year} onChange={(e) => setYear(Number(e.target.value))} disabled={generating}>
                            {years.map((y) => <option key={y} value={y}>{y}</option>)}
                        </select>
                        <button type="button" className="billing-btn billing-btn--primary" onClick={gen} disabled={generating}>{generating ? 'Generando…' : 'Generar'}</button>
                    </div>
                    <p className="billing-generate__note">Los días se derivan de las estadías de cada vaca; la tarifa vigente se congela en la liquidación.</p>
                </div>
            </Gate>

            <div className="billing-overview__bar">
                <label className="billing-form__field billing-form__field--inline">
                    <span className="billing-form__label">Año</span>
                    <select value={year} onChange={(e) => setYear(Number(e.target.value))}>
                        {years.map((y) => <option key={y} value={y}>{y}</option>)}
                    </select>
                </label>
            </div>

            {error && <div className="cat-overview__error">{error.status === 403 ? 'No tenés permiso para ver las liquidaciones.' : (error.message || 'No se pudieron cargar las liquidaciones')}</div>}

            {isLoading ? (
                <div className="cat-overview__state">Cargando liquidaciones…</div>
            ) : !batches.length ? (
                <div className="cat-overview__state"><p className="cat-overview__state-text">No hay liquidaciones en {year}.</p></div>
            ) : (
                <div className={`cat-overview__card ${isFetching ? 'cat-overview__card--busy' : ''}`}>
                    <table className="cat-table">
                        <thead>
                            <tr><th>Período</th><th>Estado</th><th>Vacas</th><th>Líneas</th><th className="cat-table__num">Total pensión</th><th aria-label="Acciones" /></tr>
                        </thead>
                        <tbody>
                            {batches.map((b) => {
                                const st = STATUS[b.status];
                                return (
                                    <tr key={b.id} className="cat-table__row">
                                        <td><Link className="cat-table__name" to={`/admin/billing/liquidations/${b.id}`}>{MONTHS[(b.period_month || 1) - 1]} {b.period_year}</Link></td>
                                        <td>{st ? <span className={`billing-tag billing-tag--${st.tone}`}>{st.label}</span> : b.status}</td>
                                        <td>{b.animal_count ?? '—'}</td>
                                        <td>{b.liquidation_count ?? '—'}</td>
                                        <td className="cat-table__num">{fmtMoney(b.totals?.pension_total)}</td>
                                        <td><Link className="billing-btn billing-btn--ghost billing-btn--sm" to={`/admin/billing/liquidations/${b.id}`}>Ver</Link></td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}
        </>
    );
}
